import { useCallback, useMemo, useState } from 'react'

import { type TeamMember } from '@api'
import { useUnit } from 'effector-react'

import { ReassignDropdown } from '@/components/layout/reassign-dropdown.tsx'
import { $hasReassignPermission } from '@/models/permissions.ts'
import type { MassMessage } from '@/modules/chatting/types.ts'
import { reassignMemberFx } from '@/modules/chatting/ui/sidebar/mass-messages-statistics/models/normalMassMessagesStats.ts'

interface Props {
  massMessage: MassMessage
  members: TeamMember[]
  isMembersFetching: boolean
}

export const MassMessageReassignDropdown = ({
  massMessage,
  members,
  isMembersFetching,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false)
  const { userId, originalUserId } = massMessage
  const assignee =
    members?.find((member) => member.userId === userId)?.name ?? 'no one'
  const hasPermission = useUnit($hasReassignPermission)
  const canReassign = !isMembersFetching && !!members.length && hasPermission
  const canDropdownOpen = canReassign && !isLoading

  const trigger = useMemo(
    () => <span className="overflow-hidden text-ellipsis">{assignee}</span>,
    [assignee],
  )

  const handleChangeAssignee = useCallback(
    async (memberId: string) => {
      try {
        setIsLoading(true)
        await reassignMemberFx({ userId: Number(memberId), massMessage })
      } catch (error) {
        console.error('Error reassigning member:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [massMessage],
  )

  return (
    <ReassignDropdown
      handleChangeAssignee={handleChangeAssignee}
      assignedUserId={userId}
      originallyAssignedUserId={originalUserId}
      canDropdownOpen={canDropdownOpen}
      trigger={trigger}
      members={members}
      useDelimiter={false}
      isLoading={isLoading}
    />
  )
}
